import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { docHasPremium } from '../../lib/docTypes';
import { API } from './utils';

export default function AIReviewDialog({ review, propertyId, property, onClose, onApplied }) {
  const [localFields, setLocalFields] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review) {
      setLocalFields({
        document_name: review.fields.document_name || '',
        expiry_date: review.fields.expiry_date || '',
        premium_amount: review.fields.premium_amount != null ? String(review.fields.premium_amount) : '',
        monthly_premium_amount: review.fields.monthly_premium_amount != null ? String(review.fields.monthly_premium_amount) : '',
        monthly_rent: review.fields.monthly_rent != null ? String(review.fields.monthly_rent) : '',
        deposit_amount: review.fields.deposit_amount != null ? String(review.fields.deposit_amount) : '',
        contract_value: review.fields.contract_value != null ? String(review.fields.contract_value) : '',
        tenancy_start_date: review.fields.tenancy_start_date || '',
        tenancy_end_date: review.fields.tenancy_end_date || '',
        tenant_name: review.fields.tenant_name || '',
        tenant_email: review.fields.tenant_email || '',
        tenant_phone: review.fields.tenant_phone || '',
        contract_type: review.fields.contract_type || '',
      });
    }
  }, [review]);

  if (!review) return null;
  const { docId, docType } = review;
  const isTenancy = docType === 'tenancy_agreement';
  const showPremium = docHasPremium(docType, '');

  const setField = (k, v) => setLocalFields((f) => ({ ...f, [k]: v }));

  const numOrNull = (s) => {
    const t = (s || '').toString().trim();
    if (t === '') return null;
    const n = parseFloat(t);
    return isNaN(n) ? null : n;
  };

  const apply = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // 1) update document
      const docPayload = {
        name: localFields.document_name?.trim() || null,
        expiry_date: localFields.expiry_date || null,
      };
      if (showPremium) {
        docPayload.premium_amount = numOrNull(localFields.premium_amount);
        docPayload.monthly_premium_amount = numOrNull(localFields.monthly_premium_amount);
      }
      await axios.put(`${API}/documents/${docId}`, docPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // 2) for tenancy: update property too
      if (isTenancy) {
        const propPayload = { ...property };
        const rent = numOrNull(localFields.monthly_rent);
        const deposit = numOrNull(localFields.deposit_amount);
        const contract = numOrNull(localFields.contract_value);
        const startDate = localFields.tenancy_start_date || null;
        const endDate = localFields.tenancy_end_date || null;
        const tName = (localFields.tenant_name || '').trim();
        const tEmail = (localFields.tenant_email || '').trim();
        const tPhone = (localFields.tenant_phone || '').trim();
        const ctype = (localFields.contract_type || '').trim();
        if (rent !== null) propPayload.rent_amount = rent;
        if (deposit !== null) propPayload.deposit_amount = deposit;
        if (contract !== null) propPayload.contract_value = contract;
        if (startDate) propPayload.tenancy_start_date = startDate;
        if (endDate) propPayload.tenancy_end_date = endDate;
        if (tName) propPayload.tenant_name = tName;
        if (tEmail) propPayload.tenant_email = tEmail;
        if (tPhone) propPayload.tenant_phone = tPhone;
        if (ctype) propPayload.contract_type = ctype;
        await axios.put(`${API}/properties/${propertyId}`, propPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success('Details applied successfully');
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="ai-review-dialog">
        <DialogHeader>
          <DialogTitle>AI Extracted Details — Please Review</DialogTitle>
          <DialogDescription>
            Confirm the details Gemini pulled from your document. Edit anything that looks wrong before applying.
          </DialogDescription>
        </DialogHeader>
        <p className="file-hint" style={{ marginTop: 0, marginBottom: 16 }}>
          Review the values below before applying. Edit any field that needs correcting.
          Empty fields will be left blank.
        </p>
        <div className="ai-review-grid">
          <div className="form-group">
            <Label>Document Name</Label>
            <Input
              value={localFields.document_name || ''}
              onChange={(e) => setField('document_name', e.target.value)}
              data-testid="ai-review-name"
            />
          </div>
          <div className="form-group">
            <Label>Expiry Date</Label>
            <Input
              type="date"
              value={localFields.expiry_date || ''}
              onChange={(e) => setField('expiry_date', e.target.value)}
              data-testid="ai-review-expiry"
            />
          </div>
          {showPremium && (
            <>
              <div className="form-group">
                <Label>Monthly Premium (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={localFields.monthly_premium_amount || ''}
                  onChange={(e) => setField('monthly_premium_amount', e.target.value)}
                  data-testid="ai-review-monthly-premium"
                />
              </div>
              <div className="form-group">
                <Label>Annual Premium (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={localFields.premium_amount || ''}
                  onChange={(e) => setField('premium_amount', e.target.value)}
                  data-testid="ai-review-premium"
                />
              </div>
            </>
          )}
          {isTenancy && (
            <>
              <div className="form-group">
                <Label>Tenant Name</Label>
                <Input
                  value={localFields.tenant_name || ''}
                  onChange={(e) => setField('tenant_name', e.target.value)}
                  data-testid="ai-review-tenant-name"
                />
              </div>
              <div className="form-group">
                <Label>Tenant Email</Label>
                <Input
                  type="email"
                  value={localFields.tenant_email || ''}
                  onChange={(e) => setField('tenant_email', e.target.value)}
                  placeholder="tenant@example.com"
                  data-testid="ai-review-tenant-email"
                />
              </div>
              <div className="form-group">
                <Label>Tenant Phone</Label>
                <Input
                  type="tel"
                  value={localFields.tenant_phone || ''}
                  onChange={(e) => setField('tenant_phone', e.target.value)}
                  placeholder="07123 456789"
                  data-testid="ai-review-tenant-phone"
                />
              </div>
              <div className="form-group">
                <Label>Contract Type</Label>
                <Input
                  value={localFields.contract_type || ''}
                  onChange={(e) => setField('contract_type', e.target.value)}
                  placeholder="AST / Licence / Company Let / Other"
                  data-testid="ai-review-contract-type"
                />
              </div>
              <div className="form-group">
                <Label>Tenancy Start Date</Label>
                <Input
                  type="date"
                  value={localFields.tenancy_start_date || ''}
                  onChange={(e) => setField('tenancy_start_date', e.target.value)}
                  data-testid="ai-review-start-date"
                />
              </div>
              <div className="form-group">
                <Label>Tenancy End Date</Label>
                <Input
                  type="date"
                  value={localFields.tenancy_end_date || ''}
                  onChange={(e) => setField('tenancy_end_date', e.target.value)}
                  data-testid="ai-review-end-date"
                />
              </div>
              <div className="form-group">
                <Label>Monthly Rent (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={localFields.monthly_rent || ''}
                  onChange={(e) => setField('monthly_rent', e.target.value)}
                  data-testid="ai-review-rent"
                />
              </div>
              <div className="form-group">
                <Label>Deposit (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={localFields.deposit_amount || ''}
                  onChange={(e) => setField('deposit_amount', e.target.value)}
                  data-testid="ai-review-deposit"
                />
              </div>
            </>
          )}
        </div>
        <div className="ai-review-actions">
          <Button onClick={onClose} className="btn-secondary" disabled={saving}>
            Skip
          </Button>
          <Button onClick={apply} className="btn-primary" disabled={saving} data-testid="ai-review-apply">
            {saving ? 'Saving…' : 'Apply Details'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
